import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Grid, Paper, Typography } from "@material-ui/core";
import moment from "moment";
import useStyles from "./styles";
import { withFirebase } from "../../components/Firebase";
import { Axis, Chart, Geom, getTheme, Interval, Legend, Tooltip } from "bizcharts";
import MaterialTable from "material-table";
import { tableIcons } from "../../components/TableIcons/TableIcons";

const IDEAL_STEAL_PRODUCTION_IN_TON = 1500;

function Dashboard(props) {
    const [items, setItems] = useState([]);
    const [reserves, setReserves] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const [progress, setProgress] = useState([]);
    const [progressLogs, setProgressLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Memoized utility functions
    const getWeekNumberFromDateTime = useCallback((period) => {
        const createdDate = new Date(period);
        return moment(createdDate).week();
    }, []);

    const getStatusPercentage = useCallback((status) => {
        const statusMap = {
            delivered: 100,
            doneFromWorkshope: 100,
            oneHundred: 100,
            ten: 10,
            twenty: 20,
            thirty: 30,
            forty: 40,
            fifty: 50,
            sixty: 60,
            seventy: 70,
            eighty: 80,
            nighty: 90,
        };
        return statusMap[status] || 0;
    }, []);

    const weeksInYear = useCallback((year) => {
        return Math.max(
            moment(new Date(year, 11, 31)).isoWeek(),
            moment(new Date(year, 11, 31 - 7)).isoWeek(),
        );
    }, []);

    const currentWeekNumber = useCallback(() => {
        return moment().format("w");
    }, []);

    const currentYear = useCallback(() => {
        return moment().year();
    }, []);

    // Memoized data processing functions
    const groupBy = useCallback((objectArray, property, year) => {
        return objectArray.reduce((acc, obj) => {
            const key = obj[property];
            if (!acc[key]) {
                acc[key] = [];
            }
            if (moment(obj.createdAt).year() === year) {
                acc[key].push(obj);
            }
            return acc;
        }, {});
    }, []);

    const cumulativeSum = useCallback((x) => {
        let sum = 0;
        return x.map(val => {
            sum += val;
            return sum;
        });
    }, []);

    // Memoized expensive calculations
    const totalItemsInStore = useMemo(() => {
        if (!profiles.length || !items.length) return 0;

        const profileMap = new Map(profiles.map(pro => [pro.uid, pro]));
        
        const itemsWeight = items.reduce((sum, item) => {
            const profile = profileMap.get(item.profile);
            if (profile) {
                const length = parseInt(item.length, 10) || 0;
                const count = parseInt(item.count, 10) || 0;
                const lengthInMeter = (length * count) / 1000;
                const weightInTon = (lengthInMeter * parseFloat(profile.weight)) / 1000;
                return sum + weightInTon;
            }
            return sum;
        }, 0);

        return itemsWeight;
    }, [items, profiles]);

    const itemsRegistredSum = useMemo(() => {
        if (!progress.length || !progressLogs.length) return [];

        const year = currentYear();
        const weeksCount = weeksInYear(year);
        const result = new Array(weeksCount + 1).fill(0);

        const groupedByProgressId = groupBy(progressLogs, "progressId", year);

        Object.values(groupedByProgressId).forEach(items => {
            const sortedItems = items
                .filter(i => moment(i.createdAt).year() === year)
                .sort((a, b) => b.createdAt - a.createdAt);

            sortedItems.forEach(item => {
                const weekNumber = getWeekNumberFromDateTime(item.createdAt);
                if (result[weekNumber] === 0) {
                    const percentageCompleted = getStatusPercentage(item.status);
                    const weight = parseInt(item.staalWeight, 10) || 0;
                    const completedWeightInKg = (percentageCompleted * weight) / 100;
                    result[weekNumber] = completedWeightInKg / 1000; // convert to ton
                }
            });
        });

        return result.filter(f => f === 0 || f);
    }, [progress, progressLogs, currentYear, weeksInYear, groupBy, getWeekNumberFromDateTime, getStatusPercentage]);

    const itemsOut = useMemo(() => {
        if (!profiles.length || !reserves.length) return [];

        const year = currentYear();
        const weeksCount = weeksInYear(year);
        const result = new Array(weeksCount + 1).fill(0);
        const profileMap = new Map(profiles.map(pro => [pro.uid, pro]));

        reserves
            .filter(reserve => 
                reserve.approved !== 2 && 
                moment(reserve.createdAt).year() === year
            )
            .forEach(item => {
                const weekNumber = getWeekNumberFromDateTime(item.createdAt);
                const profile = profileMap.get(item.profile);
                if (profile) {
                    const length = parseInt(item.length, 10) || 0;
                    const count = parseInt(item.count, 10) || 0;
                    const lengthInMeter = (length * count) / 1000;
                    const weightInTon = (lengthInMeter * parseFloat(profile.weight)) / 1000;
                    result[weekNumber] += weightInTon;
                }
            });

        return result;
    }, [reserves, profiles, currentYear, weeksInYear, getWeekNumberFromDateTime]);

    const itemsOnStore = useMemo(() => {
        if (!profiles.length || !items.length) return [];

        const year = currentYear();
        const weeksCount = weeksInYear(year);
        const result = new Array(weeksCount + 1).fill(0);
        const profileMap = new Map(profiles.map(pro => [pro.uid, pro]));

        items
            .filter(i => moment(i.createdAt).year() === year)
            .forEach(item => {
                const profile = profileMap.get(item.profile);
                const weekNumber = getWeekNumberFromDateTime(item.createdAt);
                const length = parseInt(item.length, 10) || 0;
                const count = parseInt(item.count, 10) || 0;
                const lengthInMeter = (length * count) / 1000;

                if (profile) {
                    const weightInTon = (lengthInMeter * parseFloat(profile.weight)) / 1000;
                    result[weekNumber] += weightInTon;
                }
            });

        return result;
    }, [items, profiles, currentYear, weeksInYear, getWeekNumberFromDateTime]);

    const weeksOfAYear = useMemo(() => {
        const year = currentYear();
        const weeks = [];
        const dataInWeek = cumulativeSum(itemsRegistredSum);

        for (let i = 0; i < itemsRegistredSum.length; i++) {
            weeks.push(i);
        }

        return { weeks, DataInWeek: dataInWeek };
    }, [itemsRegistredSum, currentYear, cumulativeSum]);

    const lastYearWeeks = useMemo(() => {
        const year = currentYear() - 1;
        const weeks = [];
        const dataInWeek = cumulativeSum(itemsRegistredSum);

        for (let i = 0; i < itemsRegistredSum.length; i++) {
            weeks.push(i);
        }

        return { weeks, DataInWeek: dataInWeek };
    }, [itemsRegistredSum, currentYear, cumulativeSum]);

    const chartData = useMemo(() => {
        const year = currentYear();
        const weeksCount = weeksInYear(year);
        
        const ideal = Array.from({ length: weeksCount }, (_, i) => ({
            week: i + 1,
            type: "Ideelt",
            ton: (i * parseFloat(IDEAL_STEAL_PRODUCTION_IN_TON / weeksCount)).toFixed(2),
        }));

        const realDataLastYear = lastYearWeeks.weeks.map((m, i) => ({
            week: m + 1,
            type: (year - 1).toString(),
            ton: parseFloat(lastYearWeeks.DataInWeek[m] || 0).toFixed(2)
        }));

        return ideal.concat(realDataLastYear);
    }, [weeksOfAYear, lastYearWeeks, currentYear, weeksInYear]);

    const onStoreAndReservedOutData = useMemo(() => {
        const weekNum = parseInt(currentWeekNumber());
        const data = [];

        for (let i = 1; i <= weekNum; i++) {
            data.push({
                name: "På lager",
                period: "uke-" + i,
                value: itemsOnStore[i] || 0,
            });
        }

        for (let i = 1; i <= weekNum; i++) {
            data.push({
                name: "Reservert",
                period: "uke-" + i,
                value: (itemsOut[i] || 0).toFixed(2),
            });
        }

        return data;
    }, [itemsOnStore, itemsOut, currentWeekNumber]);

    const itemsDeliveryingThisWeek = useMemo(() => {
        const weekNumber = currentWeekNumber();
        const year = currentYear();
        return progress.filter(p => 
            moment(p.createdAt).year() === year && 
            moment(p.staalDeliveryDate).week() == weekNumber
        );
    }, [progress, currentWeekNumber, currentYear]);

    const itemsToBeDeliveredThisWeek = useMemo(() => {
        const weekNumber = currentWeekNumber();
        const year = currentYear();
        return progress.filter(p => 
            moment(p.createdAt).year() === year && 
            moment(p.staalExpireDate).week() == weekNumber
        );
    }, [progress, currentWeekNumber, currentYear]);

    const producedThisYear = useMemo(() => {
        const weekNum = parseInt(currentWeekNumber());
        return weeksOfAYear.DataInWeek[weekNum] 
            ? weeksOfAYear.DataInWeek[weekNum].toFixed(2)
            : 0;
    }, [weeksOfAYear, currentWeekNumber]);

    // Firebase listeners with cleanup
    useEffect(() => {
        const listeners = [];

        const setupListeners = () => {
            // Profiles listener
            const profilesListener = props.firebase
                .profiles()
                .orderByChild("createdAt")
                .on("value", snapshot => {
                    const profileObject = snapshot.val();
                    if (profileObject) {
                        const profileList = Object.keys(profileObject).map(key => ({
                            ...profileObject[key],
                            uid: key,
                        }));
                        setProfiles(profileList);
                    }
                });
            listeners.push(() => props.firebase.profiles().off("value", profilesListener));

            // Items listener
            const itemsListener = props.firebase
                .items()
                .orderByChild("createdAt")
                .on("value", snapshot => {
                    const itemObject = snapshot.val();
                    if (itemObject) {
                        const itemList = Object.keys(itemObject).map(key => ({
                            ...itemObject[key],
                            uid: key,
                        }));
                        setItems(itemList);
                    }
                });
            listeners.push(() => props.firebase.items().off("value", itemsListener));

            // Reserves listener
            const reservesListener = props.firebase
                .reserves()
                .orderByChild("createdAt")
                .on("value", snapshot => {
                    const surfaceObject = snapshot.val();
                    if (surfaceObject) {
                        const surfaceList = Object.keys(surfaceObject).map(key => ({
                            ...surfaceObject[key],
                            uid: key,
                        }));
                        setReserves(surfaceList);
                    }
                });
            listeners.push(() => props.firebase.reserves().off("value", reservesListener));

            // Progress listener
            const progressListener = props.firebase
                .progresses()
                .orderByChild("createdAt")
                .on("value", snapshot => {
                    const progressObject = snapshot.val();
                    if (progressObject) {
                        const progressList = Object.keys(progressObject).map(key => ({
                            ...progressObject[key],
                            uid: key,
                        }));
                        setProgress(progressList);
                    }
                });
            listeners.push(() => props.firebase.progresses().off("value", progressListener));

            // Progress logs listener
            const progressLogsListener = props.firebase
                .progressLogs()
                .orderByChild("createdAt")
                .on("value", snapshot => {
                    const progressLogsObject = snapshot.val();
                    if (progressLogsObject) {
                        const progressLogsList = Object.keys(progressLogsObject).map(key => ({
                            ...progressLogsObject[key],
                            uid: key,
                        }));
                        setProgressLogs(progressLogsList);
                    }
                });
            listeners.push(() => props.firebase.progressLogs().off("value", progressLogsListener));
        };

        setupListeners();
        setLoading(false);

        // Cleanup function
        return () => {
            listeners.forEach(cleanup => cleanup());
        };
    }, [props.firebase]);

    const columnsForDelivering = [
        { title: "Prosjektnr", field: "projectNr" },
        { title: "Dato", field: "staalDeliveryDate" }
    ];

    const columnsToBeDelivered = [
        { title: "Prosjektnr", field: "projectNr" },
        { title: "Sist dato", field: "staalExpireDate" }
    ];

    const cols = {
        month: {
            range: [0, 1],
        },
    };

    const colors = getTheme().colors10;
    const classes = useStyles();

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <Typography variant="h3" weight="medium">
                Rapport
            </Typography>
            <Grid container style={{paddingTop: 40}}>
                <Grid item xs={12}>
                    <ColumChart
                        total={totalItemsInStore.toFixed(2)}
                        producedThisYear={producedThisYear}
                        weekNumber={currentWeekNumber()}
                        itemsDeliveryingThisWeek={itemsDeliveryingThisWeek}
                    />
                </Grid>
                <Grid container style={{paddingTop: 40}} spacing={8}>
                    <Grid item xs={3}>
                        <MaterialTable
                            icons={tableIcons}
                            title="Stål leveranse"
                            columns={columnsForDelivering}
                            data={itemsDeliveryingThisWeek}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <Chart height={400} padding="auto" data={onStoreAndReservedOutData} autoFit>
                            <Interval
                                adjust={[
                                    {
                                        type: "dodge",
                                        marginRatio: 0,
                                    },
                                ]}
                                color="name"
                                position="period*value"
                            />
                            <Legend name="name" position="top" />
                            <Tooltip shared />
                        </Chart>
                    </Grid>
                </Grid>
                <Grid container style={{paddingTop: 40}} spacing={8}>
                    <Grid item xs={3}>
                        <MaterialTable
                            icons={tableIcons}
                            title="Stål ut(frist)"
                            columns={columnsToBeDelivered}
                            data={itemsToBeDeliveredThisWeek}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <Chart
                            height={500}
                            data={chartData}
                            scale={cols}
                            padding="auto"
                            autoFit
                            onAxisLabelClick={console.log}
                        >
                            <Legend
                                itemName={{
                                    style: item => {
                                        return {
                                            fill:
                                                item.name === "Ideal"
                                                    ? colors[0] 
                                                    : colors[1],
                                        };
                                    },
                                }}
                            />
                            <Axis
                                name="week"
                                visible={true}
                                label={{
                                    formatter: val => `Uke-${val}`,
                                }}
                            />
                            <Axis
                                visible={true}
                                name="Ton"
                                label={{
                                    formatter: val => `${val}`,
                                }}
                            />
                            <Tooltip
                                shared
                                showCrosshairs
                                region={null}
                                g2-tooltip-list-item={{display: "flex"}}
                            />
                            <Geom
                                type="point"
                                position="week*ton"
                                size={4}
                                shape={"circle"}
                                color={"type"}
                                style={{
                                    stroke: "#fff",
                                    lineWidth: 1,
                                }}
                            />
                            <Geom
                                type="line"
                                position="week*ton"
                                size={2}
                                color={"type"}
                                shape={"smooth"}
                            />
                        </Chart>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}

function ColumChart(props) {
    const classes = useStyles();

    return (
        <>
            <Grid container spacing={2}>
                <Paper className={classes.paper}>
                    <Typography
                        variant="h5"
                        color="initial"
                        style={{paddingTop: 10, paddingLeft: 30}}
                    >
                        Totalt på lagre
                    </Typography>
                    <Typography
                        variant="h1"
                        color="primary"
                        style={{paddingTop: 40, paddingLeft: 30}}
                    >
                        {props.total} tonn
                    </Typography>
                </Paper>
                <Paper className={classes.paper}>
                    <Typography
                        variant="h5"
                        color="initial"
                        style={{paddingTop: 10, paddingLeft: 30}}
                    >
                        {`Produsert i år (uke-${props.weekNumber}) `}
                    </Typography>
                    <Typography
                        variant="h1"
                        color="primary"
                        style={{paddingTop: 40, paddingLeft: 30}}
                    >
                        {props.producedThisYear} tonn    
                    </Typography>
                </Paper>
            </Grid>
        </>
    );
}

export default withFirebase(Dashboard);
